import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useAuth } from "../../services/auth/AuthContext";
import { Snackbar } from "@mui/material";
import { LOGIN } from "../../utils/app-routes";

interface EmailConfirmationProps {
  email?: string;
  onConfirm: (email: string, code: string) => void;
}

const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
  email: propEmail,
  onConfirm,
}) => {
  const { confirmUserCode } = useAuth();
  const [formData, setFormData] = useState({
    email: propEmail || "",
    code: "",
  });
  const [errors, setErrors] = useState<{ email?: string; code?: string }>({});
  const [snackBarOpen, setSAnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");

  useEffect(() => {
    if (propEmail) {
      setFormData((prev) => ({ ...prev, email: propEmail }));
    }
  }, [propEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { email, code } = formData;
    let tempErrors: { email?: string; code?: string } = {};

    if (!email.trim()) {
      tempErrors.email = "El correo electrónico es requerido.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      tempErrors.email = "El correo electrónico no es válido.";
    }

    if (!code.trim()) {
      tempErrors.code = "El código es requerido.";
    } else if (!/^\d{6}$/.test(code)) {
      tempErrors.code = "El código debe tener 6 dígitos numéricos.";
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length === 0) {
      const response = await confirmUserCode(email, code);
      if (response.status) {
        // {status: true, result: 'SUCCESS'} response payload
        onConfirm(email, code);
      } else {
        setSAnackBarOpen(true);
        setSnackBarMessage(
          "Correo electrónico o código de verificación no válido, inténtalo de nuevo"
        );
      }
    }
  };

  const handleCloseSnackBar = () => {
    setSAnackBarOpen(false);
  };

  const snackBar = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackBarOpen}
        onClose={handleCloseSnackBar}
        message={snackBarMessage}
        autoHideDuration={5000}
        key="snackbar-email-confirmation"
      />
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const isFormValid =
    formData.email.trim() !== "" && formData.code.trim() !== "";

  return (
    <form className="max-w-sm mx-auto p-4" onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold mb-4 text-center">
        Confirma tu Correo Electrónico
      </h2>
      <p className="mb-4 text-center">
        Ingresa {propEmail ? "" : "tu correo electrónico y "}el código de
        verificación que hemos enviado.
      </p>
      {!propEmail && (
        <TextField
          label="Correo Electrónico"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={Boolean(errors.email)}
          helperText={errors.email}
          fullWidth
          margin="normal"
        />
      )}
      {propEmail && (
        <div className="mb-4">
          <p className="text-center">Correo Electrónico:</p>
          <p className="text-center font-semibold">{formData.email}</p>
        </div>
      )}
      <TextField
        label="Código de Verificación"
        name="code"
        value={formData.code}
        onChange={handleChange}
        error={Boolean(errors.code)}
        helperText={errors.code}
        fullWidth
        margin="normal"
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className="mt-4 w-full"
        disabled={!isFormValid}
      >
        Verificar
      </Button>
      <Button className="margin w-full" href={LOGIN}>
        Iniciar sesión
      </Button>
      {snackBar()}
    </form>
  );
};

export default EmailConfirmation;
